import { useId, useRef, useState } from 'react';
import {
  Box,
  Button,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import type { PromotionView } from '@/api/generated';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { useActionError } from '@/components/useActionError';
import { useToast } from '@/components/useToast';
import { visuallyHidden } from '@/components/visuallyHidden';
import { useMenuFocusReturn } from '@/features/members/useMenuFocusReturn';
import { useCanWrite } from '@/auth/authHooks';
import { NumericCell } from '@/components/cells';
import { StatusChip } from '@/components/status/StatusChip';
import { formatCount } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useTenantCurrency, useTenantLink } from '@/tenant/useTenant';
import { resolveTokens } from '@/theme';
import { capLabel, discountLabel, durationLabel, validityLabel } from './promotionLabel';
import type { PromotionStatusChange } from './usePromotions';

export interface PromotionTableProps {
  promotions: PromotionView[];
  selectedId: number | undefined;
  busy: boolean;
  /**
   * The query string that selects a promotion (`?promotion=<id>`, the page's other params kept).
   * Its code is a link there (a history entry); a click elsewhere on the row does the same.
   */
  openSearch: (id: number) => string;
  /** History state for the code link (TMMRSL-0074). */
  openState?: unknown;
  onSetStatus: (id: number, status: PromotionStatusChange) => Promise<void>;
  onRedemptions: (promo: PromotionView) => void;
}

export function PromotionTable({
  promotions,
  selectedId,
  busy,
  openSearch,
  openState,
  onSetStatus,
  onRedemptions,
}: PromotionTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const headingId = useId();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const showToast = useToast();
  const describe = useActionError();
  const canWrite = useCanWrite();
  const link = useTenantLink();
  const currency = useTenantCurrency();
  const [ending, setEnding] = useState<PromotionView | null>(null);
  const [acting, setActing] = useState(false);
  // An ended code no longer offers End, so focus returns to the heading (DLG-004).
  const heading = useRef<HTMLHeadingElement>(null);
  const focusReturn = useMenuFocusReturn(heading);

  /** Pause / resume: reversible, so one click (TMM-B4). End is confirmed — see `handleEnd`. */
  async function handleStatus(promo: PromotionView, status: 'PAUSED' | 'ACTIVE') {
    if (promo.id === undefined) return;
    setActing(true);
    try {
      await onSetStatus(promo.id, status);
      showToast(
        t(status === 'PAUSED' ? 'promotions.toast.paused' : 'promotions.toast.resumed', {
          code: promo.code ?? '',
        }),
      );
    } catch (err) {
      showToast(describe(err).message, 'error');
    } finally {
      setActing(false);
    }
  }

  async function handleEnd() {
    if (ending?.id === undefined) return;
    await onSetStatus(ending.id, 'ENDED');
    showToast(
      t('promotions.toast.ended', {
        code: ending.code ?? '',
        n:
          ending.redemptionCount == null
            ? t('promotions.unavailable')
            : formatCount(ending.redemptionCount, locale),
      }),
    );
    focusReturn.rowGone();
  }

  if (promotions.length === 0) {
    return (
      <Paper sx={{ px: 2.5 }}>
        <EmptyState
          kind="none"
          title={t('promotions.empty')}
          action={
            canWrite ? (
              <Button variant="contained" component={RouterLink} to={link('promotions/new')}>
                {t('promotions.new')}
              </Button>
            ) : undefined
          }
        />
      </Paper>
    );
  }

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <Typography
        variant="h2"
        component="h2"
        id={headingId}
        tabIndex={-1}
        ref={heading}
        sx={visuallyHidden}
      >
        {t('promotions.tableHeading')}
      </Typography>
      <Table size="small" aria-labelledby={headingId}>
        <TableHead>
          <TableRow>
            <TableCell>{t('promotions.col.code')}</TableCell>
            <TableCell>{t('promotions.col.label')}</TableCell>
            <TableCell>{t('promotions.col.discount')}</TableCell>
            <TableCell>{t('promotions.col.duration')}</TableCell>
            <TableCell align="right">{t('promotions.col.redeemed')}</TableCell>
            <TableCell align="right">{t('promotions.col.cap')}</TableCell>
            <TableCell>{t('promotions.col.status')}</TableCell>
            <TableCell align="right">
              <Box component="span" sx={visuallyHidden}>
                {t('common.actions')}
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {promotions.map((promo) => {
            const selected = promo.id !== undefined && promo.id === selectedId;
            const live = promo.status === 'ACTIVE' || promo.status === 'PAUSED';
            const window = validityLabel(promo, locale);
            const id = promo.id;
            const code = (
              <Typography variant="mono" component="span">
                {promo.code ?? t('promotions.unavailable')}
              </Typography>
            );
            return (
              <TableRow
                key={id ?? promo.code}
                hover
                selected={selected}
                // A mouse convenience: exactly what the code's link does. The selected row is a no-op.
                onClick={() => {
                  if (id === undefined || selected) return;
                  void navigate({ search: openSearch(id) }, { state: openState });
                }}
                sx={{
                  cursor: id !== undefined ? 'pointer' : undefined,
                  bgcolor: selected ? tokens.brandSoft : undefined,
                }}
              >
                <TableCell component="th" scope="row">
                  {id === undefined ? (
                    code
                  ) : (
                    <Link
                      component={RouterLink}
                      to={{ search: openSearch(id) }}
                      state={openState}
                      aria-current={selected ? 'true' : undefined}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (selected) event.preventDefault();
                      }}
                    >
                      {code}
                    </Link>
                  )}
                </TableCell>
                <TableCell>{promo.name ?? t('promotions.unavailable')}</TableCell>
                <TableCell>{discountLabel(promo, locale, currency)}</TableCell>
                <TableCell>
                  <Box>
                    {durationLabel(promo)}
                    {window ? (
                      <Typography variant="caption" sx={{ display: 'block', color: tokens.text2 }}>
                        {window}
                      </Typography>
                    ) : null}
                  </Box>
                </TableCell>
                <NumericCell value={promo.redemptionCount} />
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {capLabel(promo, t('promotions.cap.unlimited'), locale)}
                </TableCell>
                <TableCell>
                  <StatusChip domain="promotion" status={promo.status} />
                </TableCell>
                <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                  <Box
                    sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}
                  >
                    {canWrite && promo.status === 'ACTIVE' ? (
                      <Button
                        disabled={busy || acting}
                        onClick={() => void handleStatus(promo, 'PAUSED')}
                      >
                        {t('promotions.actions.pause')}
                      </Button>
                    ) : null}
                    {canWrite && promo.status === 'PAUSED' ? (
                      <Button
                        disabled={busy || acting}
                        onClick={() => void handleStatus(promo, 'ACTIVE')}
                      >
                        {t('promotions.actions.resume')}
                      </Button>
                    ) : null}
                    {canWrite && live ? (
                      <Button
                        color="error"
                        disabled={busy || acting || promo.id === undefined}
                        onClick={(event) => {
                          focusReturn.remember(event.currentTarget);
                          setEnding(promo);
                        }}
                      >
                        {t('promotions.actions.end')}
                      </Button>
                    ) : null}
                    <Button disabled={promo.id === undefined} onClick={() => onRedemptions(promo)}>
                      {t('promotions.actions.redemptions')}
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <ConfirmDialog
        open={ending !== null}
        title={t('promotions.endConfirmTitle', { code: ending?.code ?? t('promotions.unavailable') })}
        consequence={t('promotions.endConfirm', {
          n:
            ending?.redemptionCount == null
              ? t('promotions.unavailable')
              : formatCount(ending.redemptionCount, locale),
        })}
        confirmLabel={t('promotions.actions.end')}
        destructive
        onConfirm={handleEnd}
        onCancel={() => {
          setEnding(null);
          focusReturn.dialogClosed();
        }}
        errorMessage={(err) => describe(err).message}
      />
    </Paper>
  );
}
